// Chalk Explainer Ad's script as live's form reads it (spec G14-chalk-explainer-ad, PR d1; live's
// `chalk-explainer-ad-form/library-view` `constants.ts`, `messages.ts`, `schema.ts` and
// `utils.ts`, read as a behaviour spec): live's words, the labels of the script's rows, and what
// keeps the script from approval. The server checks the same on Approve
// (`templates/chalk_script.script_issue`) and computes the timing plan the rows read.
import type {
  ChalkBoardPart,
  ChalkClip,
  ChalkClipTiming,
  ChalkColor,
  ChalkExplainerAdDraft,
  ChalkLineRole,
  ChalkLineTiming,
  ChalkScript,
  ChalkScriptLine,
  ChalkTimingPlan,
} from "../../../api/generated/model";

/** Live's words (`constants.ts`). */
export const PICK_PRODUCT = "Pick a product and its packshot";
export const GENERATE_SCRIPT = "Generate the script";
export const SCRIPT_SHAPE = "The script needs its ten lines — generate it again";
export const EMPTY_LINE = "Every line needs what is written and what is said";
export const EMPTY_BOARD = "Every line but the last needs a board part";
export const EMPTY_HEADLINE = "Write the end card headline";
export const EMPTY_BUTTON = "Fill in the CTA: it is the end card button";
export const UNREADABLE_PACK =
  "The pack was hard to read — type the product name or its text and generate again.";
export const SCRIPT_TOOLTIP =
  "Ten sentences in three clips — the problem, the product, the proof — each with what the " +
  "narrator says and what the hand writes on the board as it is said: up to 3 parts in white, " +
  "coral or yellow chalk, short capitals or a simple icon. Every product fact is quoted from the " +
  "product page.";
export const EMPTY_SCRIPT_HINT = "to write the ten sentences and the board from the product.";
export const PRODUCT_CLOSE_NOTE = "The hand sets the chalk down and raises the real product.";
export const SPOKEN_LABEL = "Says:";
export const SPOKEN_PLACEHOLDER = "What the narrator says";
export const ADD_PART = "Part";
export const REMOVE_PART = "Remove this part";
export const PART_TEXT_PLACEHOLDER = "CAPITALS";
export const PART_ICON_PLACEHOLDER = "Icon (optional)";
export const HEADLINE_LABEL = "Headline";
export const HEADLINE_PLACEHOLDER = "The product name";
export const SUPPORT_LINE_LABEL = "Line";
export const SUPPORT_LINE_PLACEHOLDER = "One supporting line";

export const ROLE_LABELS: Readonly<Record<ChalkLineRole, string>> = {
  hook: "hook",
  easy_truth: "easy truth",
  but_turn: "the turn",
  consequence: "consequence",
  twist: "twist",
  product: "product",
  mechanism: "mechanism",
  proof: "proof",
  loop: "the loop",
  cta: "call to action",
};
const CLIP_NOTES: Readonly<Record<ChalkClip, string>> = {
  A: "the problem",
  B: "wipe · the product",
  C: "wipe · proof, loop, the product",
};
export const CHALK_COLORS: readonly ChalkColor[] = ["white", "coral", "yellow"];
export const COLOR_LABELS: Readonly<Record<ChalkColor, string>> = {
  white: "White",
  coral: "Coral",
  yellow: "Yellow",
};
/** Live's most parts a line's board has (`MAX_BOARD_PARTS`). */
export const MAX_BOARD_PARTS = 3;
/** Live's ten lines (`SCRIPT_LINE_COUNT`). */
const SCRIPT_LINES = 10;
/** Live's tries of the script director (`SCRIPT_DIRECTOR_TRIES`). */
const SCRIPT_TRIES = 3;

const META = " · ";
const RANGE = "–";
const WARNING = "⚠";

/**
 * What keeps the script from approval (live's `findScriptIssue`), in its order: none yet, not
 * ten lines, a line with nothing written or said, a line but the last with no board part, no end
 * card headline, no end card button (the CTA chip or the director's).
 */
export function scriptIssue(script: ChalkScript | null, cta: string): string | null {
  if (script === null) return GENERATE_SCRIPT;
  if (script.lines.length !== SCRIPT_LINES) return SCRIPT_SHAPE;
  if (script.lines.some((line) => line.text.trim() === "" || line.spoken.trim() === "")) return EMPTY_LINE;
  if (script.lines.some((line) => line.role !== "cta" && line.board.parts.length === 0)) return EMPTY_BOARD;
  if (script.end_card.headline.trim() === "") return EMPTY_HEADLINE;
  return cta.trim() !== "" || script.end_card.cta_button.trim() !== "" ? null : EMPTY_BUTTON;
}

/** The warnings under the script (live's `scriptProblems`): the director's tries, the timing
 * plan's warnings, then the script's problems as the server last read them. */
export function scriptWarnings(draft: ChalkExplainerAdDraft): string[] {
  if (draft.script === null || draft.timing === null) return [];
  return [
    ...(draft.script_warning ? [scriptTriesWarning(SCRIPT_TRIES)] : []),
    ...draft.timing.warnings,
    ...draft.script_problems,
  ];
}

export function scriptTriesWarning(tries: number): string {
  return `Script check failed ${tries} times. Fix the lines before approving.`;
}

export function warningLine(warning: string): string {
  return `${WARNING} ${warning}`;
}

export function clipTitle(clip: ChalkClip): string {
  return `Clip ${clip}`;
}

/** A clip's header over its first line: "Clip A · the problem · 12 s · 34 words". */
export function clipHeaderLabel(timing: ChalkClipTiming): string {
  return [clipTitle(timing.clip), CLIP_NOTES[timing.clip], `${timing.duration_s} s`, `${timing.words} words`].join(
    META,
  );
}

export function endCardHeaderLabel(seconds: number): string {
  return ["End card", `${seconds} s`, "silent"].join(META);
}

/** The meta line beside "Script & board": each clip's seconds, the end card's and the total. */
export function scriptMetaLabel(timing: ChalkTimingPlan): string {
  return [
    ...timing.clips.map((clip) => `${clip.clip} ${clip.duration_s} s`),
    `end card ${timing.end_card_s} s`,
    `${timing.total_s} s`,
  ].join(META);
}

export function lineTimingLabel(timing: ChalkLineTiming): string {
  return `${timing.words} w${META}${timing.start_s}${RANGE}${timing.end_s} s`;
}

export function clipTiming(timing: ChalkTimingPlan | null, clip: ChalkClip): ChalkClipTiming | null {
  return timing?.clips.find((each) => each.clip === clip) ?? null;
}

export function lineTiming(timing: ChalkTimingPlan | null, lineId: number): ChalkLineTiming | null {
  return timing?.clips.flatMap((each) => each.lines).find((each) => each.line_id === lineId) ?? null;
}

export function firstLineOfClip(lines: readonly ChalkScriptLine[], index: number): boolean {
  const line = lines[index];
  return line !== undefined && lines[index - 1]?.clip !== line.clip;
}

/** A new board part (live's `toNewPart`): the last part's colour, else white, and no words. */
export function newPart(last: ChalkBoardPart | undefined): ChalkBoardPart {
  return { color: last?.color ?? "white", text: "", icon: "" };
}
